import { ReactNode } from 'react';
import { Card, CardBody, CardHead, FooterActions } from './Card';
import { Btn } from './Form';
import { WizardAccordionStyleProvider } from '../WizardAccordionStyleContext';

interface ArrowProps {
  direction: 'back' | 'forward';
}
const Arrow = ({ direction }: ArrowProps) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    {direction === 'back' ? (
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z" />
    ) : (
      <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
    )}
  </svg>
);

interface EditObjectStepShellProps {
  /** Sub-headline rendered in the card head row (e.g. "Title, media, signature…"). */
  sub?: ReactNode;
  /** Optional toolbar / actions slot rendered inside CardHead's right side. */
  headerRight?: ReactNode;
  /** Step body content. */
  children: ReactNode;

  /** Hide the Back button entirely (e.g. first step). */
  hideBack?: boolean;
  /** Label of the previous step — formats as "Back to {prevLabel}". */
  prevLabel?: string;
  onBack?: () => void;

  /** Whether to show the "Skip to Review" middle action. */
  showSkip?: boolean;
  onSkip?: () => void;

  /** Override of the primary button label (defaults to "Continue to {nextLabel}"). */
  primaryLabel?: string;
  /** Label of the next step — formats as "Continue to {nextLabel}". */
  nextLabel?: string;
  onContinue?: () => void;
  /** Hide the primary continue button (e.g. on the final Review step). */
  hideContinue?: boolean;

  /**
   * Top-right "Finish" action shown on every step when provided. Mirrors
   * UploadObject's `wizard-finished-button` — navigates back to the catalog
   * and relies on each section's unmount-flush to persist any pending edits.
   */
  onFinish?: () => void;
  /** Optional override for the Finish button label. Defaults to "Finished". */
  finishLabel?: string;
}

/**
 * Hi-Fi step-card chrome. Wraps a step body in `<Card>` with header, body,
 * and footer actions matching the design's `.card`/`.footer-actions`
 * pattern. Each step (`UploadImagesStep`, `DescribeStep`, …) renders this
 * once and slots its content into `children`. EditObject owns the
 * navigation state and passes handlers + step labels in.
 */
export const EditObjectStepShell = ({
  sub,
  headerRight,
  children,
  hideBack,
  prevLabel,
  onBack,
  showSkip,
  onSkip,
  primaryLabel,
  nextLabel,
  onContinue,
  hideContinue,
  onFinish,
  finishLabel = 'Finished',
}: EditObjectStepShellProps) => {
  const backLabel = prevLabel ? `Back to ${prevLabel}` : 'Back';
  const continueLabel =
    primaryLabel ?? (nextLabel ? `Continue to ${nextLabel}` : 'Continue');

  const finishButton = onFinish ? (
    <Btn
      variant="outlined"
      onClick={onFinish}
      data-testid="edit-object-finished-button"
    >
      {finishLabel}
    </Btn>
  ) : null;

  // Compose the header-right area: caller-provided content first, then the
  // Finish button so it lands at the far right.
  const headRight =
    headerRight || finishButton ? (
      <span className="head-actions">
        {headerRight}
        {finishButton}
      </span>
    ) : undefined;

  return (
    <Card>
      <CardHead sub={sub} right={headRight} />
      <CardBody>
        {/* Apply the Hi-Fi 'describe' WizardAccordion variant to all step
            bodies — gives every legacy MUI accordion the off-white +
            serif red title styling consistent with the new design,
            without needing per-step provider wrapping. */}
        <WizardAccordionStyleProvider variant="describe">
          {children}
        </WizardAccordionStyleProvider>
      </CardBody>
      <FooterActions
        back={
          !hideBack && onBack ? (
            <Btn variant="ghost" onClick={onBack}>
              <Arrow direction="back" />
              {backLabel}
            </Btn>
          ) : null
        }
        middle={
          showSkip && onSkip ? (
            <Btn variant="ghost" onClick={onSkip}>
              Skip to Review
            </Btn>
          ) : null
        }
        primary={
          !hideContinue && onContinue ? (
            <Btn variant="primary" onClick={onContinue}>
              {continueLabel}
              <Arrow direction="forward" />
            </Btn>
          ) : null
        }
      />
    </Card>
  );
};

/** Common nav props that EditObject passes down to every step component. */
export interface StepNavProps {
  hideBack?: boolean;
  prevLabel?: string;
  onBack?: () => void;
  showSkip?: boolean;
  onSkip?: () => void;
  primaryLabel?: string;
  nextLabel?: string;
  onContinue?: () => void;
  hideContinue?: boolean;
}
